import { useFormContext } from 'react-hook-form';
import FormField from '../../containers/FormField/FormField';
import {
  VALUE_TOO_LONG,
  VALUE_TOO_SHORT,
  VALUE_INVALID,
  VALUE_ADDRESS,
  VALUE_CITY,
  ONLY_NUMBERS,
  FIELD_MISSING,
  INVALID_PHONE,
} from './../../constants/validationMessages';
import { Col } from 'react-bootstrap';

const PrescribingLocationForm = ({
  index,
  registerForm,
  isDisabled,
}: {
  index: number;
  registerForm?: boolean;
  isDisabled?: boolean;
}) => {
  const {
    watch,
    setValue,
    trigger,
    formState: { errors },
  } = useFormContext();
  const dea = watch('provider:location:dea');

  const copyPrev = () => {
    setValue(`${index}:provider:location:dea`, dea);
    trigger(`${index}:provider:location:dea`);
  };

  return (
    <>
      <Col
        sm={6}
        md={6}
      >
        <FormField
          index={index}
          isDisabled={isDisabled}
          type="text"
          identifier="provider:location:name"
          validation={{
            maxLength: {
              value: 35,
              message: VALUE_TOO_LONG,
            },
          }}
        />
      </Col>
      <Col
        sm={6}
        md={6}
        className="p-0 d-flex align-items-flex-start"
      >
        <div className="mt-0 mt-sm-3">
          <FormField
            index={index}
            isDisabled={isDisabled}
            type="checkbox"
            identifier="provider:location:isDefault"
            radioVariant="horizontal"
            setFormValue={setValue}
            registerForm={registerForm}
          />
        </div>
      </Col>
      <Col
        sm={6}
        md={6}
        xl={3}
      >
        <FormField
          index={index}
          isDisabled={isDisabled}
          type="text"
          identifier="provider:location:address1"
          validation={{
            pattern: {
              value: /^[a-z A-Z0-9&#.,;:']+$/g,
              message: VALUE_ADDRESS,
            },
            maxLength: {
              value: 35,
              message: VALUE_TOO_LONG,
            },
          }}
        />
      </Col>
      <Col
        sm={6}
        md={6}
        xl={3}
      >
        <FormField
          index={index}
          isDisabled={isDisabled}
          type="text"
          identifier="provider:location:address2"
          validation={{
            pattern: {
              value: /^[a-z A-Z0-9&#.,;:']+$/g,
              message: VALUE_ADDRESS,
            },
            maxLength: {
              value: 35,
              message: VALUE_TOO_LONG,
            },
          }}
        />
      </Col>
      <Col
        sm={4}
        md={4}
        xl={2}
      >
        <FormField
          index={index}
          isDisabled={isDisabled}
          type="text"
          identifier="provider:location:city"
          validation={{
            pattern: {
              value: /^[a-z A-Z,-.;'&/()]*$/,
              message: VALUE_CITY,
            },
            maxLength: {
              value: 35,
              message: VALUE_TOO_LONG,
            },
          }}
        />
      </Col>
      <Col
        sm={4}
        md={4}
        xl={2}
      >
        <FormField
          index={index}
          isDisabled={isDisabled}
          type="select"
          setFormValue={setValue}
          identifier="provider:location:state"
          className="mb-1"
        />
      </Col>
      <Col
        sm={4}
        md={4}
        xl={2}
      >
        <FormField
          index={index}
          isDisabled={isDisabled}
          type="text"
          identifier="provider:location:zip"
          validation={{
            pattern: {
              value:
                /^((\d{5}-\d{4})|(\d{5})|(\d{9})|([a-zA-Z]\d[a-zA-Z]\s\d[a-zA-Z]\d)|([a-zA-Z]\d[a-zA-Z]\d[a-zA-Z]\d))$/,
              message: FIELD_MISSING,
            },
            minLength: {
              value: 5,
              message: VALUE_TOO_SHORT,
            },
            maxLength: {
              value: 10,
              message: VALUE_TOO_LONG,
            },
          }}
        />
      </Col>
      <Col
        sm={4}
        md={4}
        xl={2}
      >
        <FormField
          index={index}
          isDisabled={isDisabled}
          type="text"
          identifier="provider:location:phone"
          variant="phone"
          validation={{
            validate: (value: string) => {
              return !value.includes('_') || !/\d/.test(value) || INVALID_PHONE;
            },
          }}
        />
      </Col>
      <Col
        sm={4}
        md={4}
        xl={1}
      >
        <FormField
          index={index}
          isDisabled={isDisabled}
          type="text"
          identifier="provider:location:phoneExt"
          validation={{
            pattern: {
              value: /^(0|[1-9]\d*)(\.\d+)?$/,
              message: ONLY_NUMBERS,
            },
            maxLength: {
              value: 5,
              message: VALUE_TOO_LONG,
            },
          }}
        />
      </Col>
      <Col
        sm={4}
        md={4}
        xl={3}
      >
        <FormField
          index={index}
          isDisabled={isDisabled}
          type="text"
          identifier="provider:location:fax"
          variant="phone"
          validation={{
            validate: (value: string) => {
              return !value.includes('_') || !/\d/.test(value) || INVALID_PHONE;
            },
          }}
        />
      </Col>
      <Col
        sm={4}
        md={6}
        xl={3}
      >
        <FormField
          index={index}
          isDisabled={isDisabled}
          type="text"
          identifier="provider:location:officeContact"
        />
      </Col>
      <Col
        sm={4}
        md={6}
        xl={3}
      >
        <FormField
          index={index}
          isDisabled={isDisabled}
          type="text"
          identifier="provider:location:officePhone"
          variant="phone"
          validation={{
            validate: (value: string) => {
              return !value.includes('_') || !/\d/.test(value) || INVALID_PHONE;
            },
          }}
        />
      </Col>

      <Col
        sm={4}
        md={4}
        lg={3}
      >
        <FormField
          index={index}
          isDisabled={isDisabled}
          type="text"
          identifier="provider:location:dea"
          validation={{
            pattern: {
              value: /^[a-zA-Z]{2}[0-9]{7}$/,
              message: VALUE_INVALID,
            },
            minLength: {
              value: 9,
              message: VALUE_TOO_SHORT,
            },
            maxLength: {
              value: 9,
              message: VALUE_TOO_LONG,
            },
          }}
        />

        {index !== 0 && (
          <div
            className={`${
              Object.keys(errors).includes(`${index}:provider:location:dea`) ? 'mt-3' : ''
            } `}
          >
            <a
              id={`${index}-same-as-prev-location-anchor-register`}
              onClick={isDisabled ? undefined : copyPrev}
              className={
                isDisabled ? 'small link-disabled text-purple' : 'small link text-link text-purple'
              }
            >
              Same as previous location
            </a>
          </div>
        )}
      </Col>
      <Col
        sm={4}
        md={4}
        lg={3}
      >
        <FormField
          index={index}
          isDisabled={isDisabled}
          type="text"
          identifier="provider:location:stateLicense"
        />
      </Col>
      <Col
        sm={4}
        md={4}
        lg={3}
      >
        <FormField
          index={index}
          isDisabled={isDisabled}
          type="select"
          setFormValue={setValue}
          identifier="provider:location:LicenseState"
          className="mb-2"
        />
      </Col>
      <div className="border-bottom border-1 my-2"></div>
    </>
  );
};

export default PrescribingLocationForm;
